import type { Assessment } from "../assessment-types";

export const DISSOCIATION_ASSESSMENT: Assessment = {
  slug: "dissociation",
  emoji: "🌫️",
  title: "Dissociation Experiences",
  tagline: "Moments of distance from yourself or the world",
  intro:
    "This reflects on moments of feeling distant, foggy or disconnected from yourself, your body or your surroundings. These experiences are more common than people think, and they often make sense as a way of coping.",
  questions: [
    { id: "q1", text: "Do you find yourself zoning out during conversations or tasks without meaning to?" },
    { id: "q2", text: "Do you lose track of time, unsure where the last while went?" },
    { id: "q3", text: "Does the world around you sometimes feel unreal, distant, or dreamlike?" },
    { id: "q4", text: "Do you feel disconnected from your own body, as if you're watching yourself from outside?" },
    { id: "q5", text: "Do you find gaps in your memory of ordinary events, not just stressful ones?" },
    { id: "q6", text: "Do you go somewhere in your mind that feels far away when things get overwhelming?" },
    { id: "q7", text: "Do familiar places or people sometimes feel strange or unfamiliar for a moment?" },
    { id: "q8", text: "Do you find it hard to feel fully present, even in ordinary daily moments?" },
    { id: "q9", text: "Do you notice yourself daydreaming as a way of getting through a hard moment?" },
    { id: "q10", text: "Does your voice or body sometimes feel like it isn't quite yours?" },
    { id: "q11", text: "Do you have trouble recalling details of things you did earlier the same day?" },
    { id: "q12", text: "Do you feel like you're on autopilot for large parts of the day?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "You seem to feel fairly present and connected most of the time.",
      body: "Occasional daydreaming or zoning out is normal for most people. It's still worth noticing what tends to pull your attention away.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "You may recognise some patterns of drifting away or feeling foggy at times.",
      body: "This is a common response when the mind needs a small break from something overwhelming. Gentle grounding, using the senses to reconnect with the present, can help bring you back.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "You may recognise a pattern of feeling distant from yourself or your surroundings fairly often.",
      body: "This kind of distancing often develops as a way of coping with situations that felt like too much to stay fully present for. It made sense at the time as a form of protection. Practising staying gently with the present moment, in small doses, can help.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "You may recognise a strong pattern of feeling disconnected from yourself, your body or your surroundings.",
      body: "Experiencing this often can be disorienting and even frightening. It usually reflects a history where drifting away felt safer than staying present. This is an adaptation, not a weakness, and there are ways to gently build a stronger sense of being here.",
    },
  ],
  next: [
    { label: "Understanding dissociation", to: "/learn/dissociation" },
    { label: "What triggers this response", to: "/learn/triggers" },
    { label: "Try a grounding exercise", to: "/grounding" },
    { label: "Try a somatic practice", to: "/somatic" },
  ],
};
